import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn, formatCurrency } from '@/lib/utils';
import { calculatePlayerStats } from '@/lib/player-stats';
import type { PlayerLeaderboardEntry } from '@/ui/view-props';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Trophy, TrendingUp, Calendar, Target, Users } from 'lucide-react';
import { subMonths, isAfter, format } from 'date-fns';
import { useUserSettings } from '@/lib/user-settings-context';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { usePlayers } from '@/hooks/use-players';
import { useAllSessionPlayers } from '@/hooks/use-session-players';
import { useExpenses } from '@/hooks/use-expenses';

export default function Statistics() {
  const { user } = useAuth();
  const { targetUserId, isPublic } = useViewMode();
  const { settings } = useUserSettings();
  const navigate = useNavigate();

  const { data: rawPlayers = [], isLoading: playersLoading } = usePlayers(targetUserId);
  const { data: rawSpData = [], isLoading: spLoading } = useAllSessionPlayers(targetUserId);
  const { data: rawExpenses = [], isLoading: expensesLoading } = useExpenses(targetUserId);

  const loading = playersLoading || spLoading || expensesLoading;
  const [filter, setFilter] = useState<'lifetime' | '3months' | '1month' | 'season'>('lifetime');

  const monthlyData = useMemo(() => {
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      
      // Count sessions for this month
      const monthStr = format(d, 'MMM');
      const sessionsInMonth = rawSpData.filter(sp => {
        const sessDate = new Date((sp.sessions as any)?.date);
        return format(sessDate, 'MMM') === monthStr && sessDate.getFullYear() === d.getFullYear();
      });
      
      // We want unique session IDs
      const uniqueSessions = new Set(sessionsInMonth.map(sp => sp.session_id)).size;

      last6Months.push({
        month: monthStr,
        sessions: uniqueSessions,
        date: d,
      });
    }
    return last6Months;
  }, [rawSpData]);

  const getCurrentQuarter = () => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) + 1;
    const qStart = new Date(now.getFullYear(), (q - 1) * 3, 1);
    const qEnd = new Date(now.getFullYear(), q * 3, 0);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return {
      label: `Q${q} ${now.getFullYear()}`,
      range: `${monthNames[qStart.getMonth()]} – ${monthNames[qEnd.getMonth()]}`,
      start: qStart,
      end: qEnd,
    };
  };

  // ✅ Player click navigation (safe & clean)
  const handlePlayerClick = (playerId: string) => {
    const path = isPublic
      ? `/public/${targetUserId}/player/${playerId}`
      : `/player/${playerId}`;
    navigate(path);
  };

  const leaderboard = useMemo(() => {
    if (loading || !rawPlayers.length) return [];

    let filteredSpData = rawSpData;
    let filteredExpenses = rawExpenses;

    if (settings.date_range_filters) {
      if (filter === '3months') {
        const threeMonthsAgo = subMonths(new Date(), 3);
        filteredSpData = rawSpData.filter(sp => isAfter(new Date((sp.sessions as any)?.date), threeMonthsAgo));
        filteredExpenses = rawExpenses.filter(exp => isAfter(new Date(exp.created_at), threeMonthsAgo));
      } else if (filter === '1month') {
        const oneMonthAgo = subMonths(new Date(), 1);
        filteredSpData = rawSpData.filter(sp => isAfter(new Date((sp.sessions as any)?.date), oneMonthAgo));
        filteredExpenses = rawExpenses.filter(exp => isAfter(new Date(exp.created_at), oneMonthAgo));
      } else if (filter === 'season' && settings.seasonal_leaderboard) {
        const { start: seasonStart, end: seasonEnd } = getCurrentQuarter();
        filteredSpData = rawSpData.filter(sp => {
          const d = new Date((sp.sessions as any)?.date);
          return d >= seasonStart && d <= seasonEnd;
        });
        filteredExpenses = rawExpenses.filter(exp => {
          const d = new Date(exp.created_at);
          return d >= seasonStart && d <= seasonEnd;
        });
      }
    }

    const expenseMap: Record<string, number> = {};
    filteredExpenses.forEach(exp => {
      if (!expenseMap[exp.payer_id]) expenseMap[exp.payer_id] = 0;
      expenseMap[exp.payer_id] += Number(exp.amount);
      
      exp.participants?.forEach((p: any) => {
        if (!expenseMap[p.player_id]) expenseMap[p.player_id] = 0;
        expenseMap[p.player_id] -= Number(p.share);
      });
    });

    const leaderboardData: (PlayerLeaderboardEntry & { totalBuyIns: number })[] = rawPlayers.map(player => {
      const playerSessions = filteredSpData
        .filter(sp => sp.player_id === player.id)
        .map(sp => ({
          net: sp.net_amount || 0,
          buyIns: sp.buy_ins,
          buyInUnit: (sp.sessions as any)?.buy_in_unit,
          date: (sp.sessions as any)?.date
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      const stats = calculatePlayerStats(playerSessions, expenseMap[player.id] || 0);
      const totalBuyIns = playerSessions.reduce((sum, s) => sum + (s.buyIns || 0), 0);
      return {
        ...stats,
        id: player.id,
        name: player.name,
        nickname: player.nickname,
        totalBuyIns,
      };
    });

    return leaderboardData.sort((a, b) => b.totalNet - a.totalNet);
  }, [rawPlayers, rawSpData, rawExpenses, filter, settings.date_range_filters, settings.seasonal_leaderboard]);

  const CATEGORY_ICONS: Record<string, string> = {
    Food: '🍕', Drinks: '🍺', Venue: '🏠', Travel: '🚗', Other: '📦',
  };

  const expenseCategoryData = useMemo(() => {
    let filteredExpenses = rawExpenses;
    if (settings.date_range_filters) {
      if (filter === '3months') {
        filteredExpenses = rawExpenses.filter(exp => isAfter(new Date(exp.created_at), subMonths(new Date(), 3)));
      } else if (filter === '1month') {
        filteredExpenses = rawExpenses.filter(exp => isAfter(new Date(exp.created_at), subMonths(new Date(), 1)));
      } else if (filter === 'season') {
        const now = new Date();
        const q = Math.floor(now.getMonth() / 3);
        const seasonStart = new Date(now.getFullYear(), q * 3, 1);
        filteredExpenses = rawExpenses.filter(exp => isAfter(new Date(exp.created_at), seasonStart));
      }
    }
    const totals: Record<string, number> = {};
    filteredExpenses.forEach(exp => {
      const cat = (exp as any).category || 'Other';
      totals[cat] = (totals[cat] || 0) + Number(exp.amount);
    });
    return Object.entries(totals).map(([name, total]) => ({ name, total, icon: CATEGORY_ICONS[name] ?? '📦' }));
  }, [rawExpenses, filter, settings.date_range_filters]);

  const winRateChartConfig = {
    winRate: { label: "Win Rate", color: "hsl(var(--primary))" },
  };

  const activityChartConfig = {
    sessions: { label: "Entries", color: "hsl(var(--primary))" },
  };

  const expenseBreakdownChartConfig = {
    total: { label: "Amount", color: "hsl(var(--primary))" },
  };

  if (loading && rawPlayers.length === 0) {
    return (
      <div className="py-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="py-6 space-y-6 pb-24">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
          <TrendingUp className="h-5 w-5 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight">Statistics</h1>
          <p className="text-sm text-muted-foreground">Detailed performance analysis</p>
        </div>
        {settings.head_to_head && (
          <div className="ml-auto">
            <Link to={isPublic ? `/public/${targetUserId}/head-to-head` : "/head-to-head"}>
              <Button variant="outline" size="sm" className="gap-2">
                <Users className="h-4 w-4" />
                Compare
              </Button>
            </Link>
          </div>
        )}
      </div>

      {settings.date_range_filters && (
        settings.ui_variant === 'neon' ? (
          <div className="neon-tabs">
            <button className={cn("neon-tab", filter === 'lifetime' && "active")} onClick={() => setFilter('lifetime')}>Lifetime</button>
            <button className={cn("neon-tab", filter === '3months' && "active")} onClick={() => setFilter('3months')}>3 Months</button>
            <button className={cn("neon-tab", filter === '1month' && "active")} onClick={() => setFilter('1month')}>1 Month</button>
            {settings.seasonal_leaderboard && (
              <button className={cn("neon-tab", filter === 'season' && "active")} onClick={() => setFilter('season')}>Season</button>
            )}
          </div>
        ) : (
          <Tabs value={filter} className="w-full" onValueChange={(v) => setFilter(v as any)}>
            <TabsList className={cn("grid w-full", settings.seasonal_leaderboard ? "grid-cols-4" : "grid-cols-3")}>
              <TabsTrigger value="lifetime">Lifetime</TabsTrigger>
              <TabsTrigger value="3months">3 Months</TabsTrigger>
              <TabsTrigger value="1month">1 Month</TabsTrigger>
              {settings.seasonal_leaderboard && <TabsTrigger value="season">Season</TabsTrigger>}
            </TabsList>
          </Tabs>
        )
      )}

      {/* Season label */}
      {filter === 'season' && settings.seasonal_leaderboard && (() => {
        const q = getCurrentQuarter();
        return (
          <div className="text-center">
            <span className="text-sm font-semibold">{q.label}</span>
            <span className="text-sm text-muted-foreground ml-2">· {q.range}</span>
          </div>
        );
      })()}

      {/* Podium */}
      {settings.global_podium && leaderboard.length >= 3 && (
        <div className="flex items-end justify-center gap-2 px-2 pt-8 pb-4">
          {/* 2nd Place */}
          <div className="flex flex-col items-center gap-2 flex-1 max-w-[100px] cursor-pointer" 
               onClick={() => handlePlayerClick(leaderboard[1].id)}>
            <div className="text-center">
              <p className="text-xs font-bold truncate w-full px-1">{leaderboard[1].name}</p>
              <p className="text-[10px] text-success font-mono font-bold">{formatCurrency(leaderboard[1].totalNet)}</p>
            </div>
            <div className={cn(
              "w-full h-24 rounded-t-lg flex flex-col items-center justify-start pt-3 shadow-inner relative overflow-hidden border-t border-x",
              settings.ui_variant === 'neon' ? "podium-silver" : "bg-slate-300 dark:bg-slate-700"
            )}>
              <span className="text-3xl filter drop-shadow-sm">🥈</span>
              <span className={cn(
                "font-black text-xl mt-1",
                settings.ui_variant === 'neon' ? "text-slate-400" : "text-slate-500 dark:text-slate-400"
              )}>2</span>
            </div>
          </div>

          {/* 1st Place */}
          <div className="flex flex-col items-center gap-2 flex-1 max-w-[120px] -mt-8 cursor-pointer" 
               onClick={() => handlePlayerClick(leaderboard[0].id)}>
            <div className="text-center">
              <p className="text-sm font-black truncate w-full px-1">{leaderboard[0].name}</p>
              <p className="text-xs text-success font-mono font-bold">{formatCurrency(leaderboard[0].totalNet)}</p>
            </div>
            <div className={cn(
              "w-full h-32 rounded-t-lg flex flex-col items-center justify-start pt-4 shadow-lg relative overflow-hidden border-x-4 border-t-4",
              settings.ui_variant === 'neon' ? "podium-gold" : "bg-yellow-400 dark:bg-yellow-600 border-yellow-200/30"
            )}>
              <span className="text-4xl filter drop-shadow-md">🥇</span>
              <span className={cn(
                "font-black text-2xl mt-1",
                settings.ui_variant === 'neon' ? "text-yellow-500" : "text-yellow-700 dark:text-yellow-100"
              )}>1</span>
            </div>
          </div>

          {/* 3rd Place */}
          <div className="flex flex-col items-center gap-2 flex-1 max-w-[100px] cursor-pointer" 
               onClick={() => handlePlayerClick(leaderboard[2].id)}>
            <div className="text-center">
              <p className="text-xs font-bold truncate w-full px-1">{leaderboard[2].name}</p>
              <p className="text-[10px] text-success font-mono font-bold">{formatCurrency(leaderboard[2].totalNet)}</p>
            </div>
            <div className={cn(
              "w-full h-16 rounded-t-lg flex flex-col items-center justify-start pt-2 shadow-inner relative overflow-hidden border-t border-x",
              settings.ui_variant === 'neon' ? "podium-bronze" : "bg-orange-300 dark:bg-orange-800"
            )}>
              <span className="text-2xl filter drop-shadow-sm">🥉</span>
              <span className={cn(
                "font-black text-lg mt-0.5",
                settings.ui_variant === 'neon' ? "text-orange-500" : "text-orange-600 dark:text-orange-200"
              )}>3</span>
            </div>
          </div>
        </div>
      )}

      {/* Global Leaderboard - Clickable */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="p-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-yellow-500" />
            Global Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Rank</th>
                  <th className="text-left py-2 px-4 font-medium text-muted-foreground">Player</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground">Sess</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground">Spent</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground">Net</th>
                  <th className="text-right py-2 px-4 font-medium text-muted-foreground">Tier</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((p, i) => (
                  <tr
                    key={p.id}
                    className={cn(
                      "border-b border-border/50 last:border-0 hover:bg-muted/10 transition-colors cursor-pointer",
                      i === 0 && settings.ui_variant === 'neon' && "animate-winner-glow podium-gold"
                    )}
                    onClick={() => handlePlayerClick(p.id)}
                  >
                    <td className="py-3 px-4">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                    </td>
                    <td className="py-3 px-4">
                      <div className="font-bold">{p.name}</div>
                      {p.nickname && <div className="text-xs text-muted-foreground">"{p.nickname}"</div>}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-muted-foreground text-xs">
                      {p.totalSessions}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-muted-foreground text-xs">
                      {formatCurrency(p.totalSpent)}
                    </td>
                    <td className={cn(
                      'py-3 px-4 text-right font-mono font-bold',
                      p.totalNet > 0 ? 'text-success' : p.totalNet < 0 ? 'text-destructive' : 'text-muted-foreground'
                    )}>
                      {p.totalNet >= 0 ? '+' : ''}{formatCurrency(p.totalNet)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Badge variant="outline" className={cn("text-[10px] font-bold", p.rankColor)}>
                        {p.rank}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Charts - Kept exactly as original */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Win Rate Analysis */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="p-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" />
              Win Rate Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <ChartContainer config={winRateChartConfig} className="h-[200px] w-full">
              <BarChart data={leaderboard.slice(0, 5)}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} unit="%" />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="winRate" fill="var(--color-winRate)" radius={[4, 4, 0, 0]}>
                  {leaderboard.slice(0, 5).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.winRate > 50 ? 'hsl(var(--success))' : 'hsl(var(--primary))'} />
                  ))}
                </Bar>
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Activity Volume */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="p-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-orange-500" />
              Activity Volume
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <ChartContainer config={activityChartConfig} className="h-[200px] w-full">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="sessions" fill="var(--color-sessions)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Expense Breakdown by Category */}
      {expenseCategoryData.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="p-4">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="text-lg">📦</span>
              Expense Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <ChartContainer config={expenseBreakdownChartConfig} className="h-[200px] w-full">
              <BarChart data={expenseCategoryData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false}
                  tickFormatter={(name) => `${CATEGORY_ICONS[name] ?? '📦'} ${name}`} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(v: number) => formatCurrency(v)}
                  contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }}
                />
                <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}